/** Read-only projection of existing typed proposal and execution records. */
export interface ABVReference {
  execution_id: string; revision: number; fingerprint: string; target_system_id: number;
  build_execution_id?: string;
}
export interface ABVSnapshot {
  sessionId: string; reference: ABVReference | null; workflowStatus: string | null;
  policyFacts: Record<string, any> | null; activityId: string | null;
  running: boolean; methods: { key: string; name: string; kind: string; inputs: string[]; prompt: string | null;
    refreshPolicy?: string | null }[];
  /** Operating selection saved with the design draft (backend-bound identity),
   *  or null when the loaded package carries none. */
  operatingSelection: { accountId: string; symbol: string } | null;
}
export const emptyABVSnapshot = (sessionId: string): ABVSnapshot => ({
  sessionId, reference: null, workflowStatus: null, policyFacts: null, activityId: null, running: false, methods: [],
  operatingSelection: null,
});
const sameReference = (a: any, b: any) => a && b && ['execution_id', 'revision', 'fingerprint', 'target_system_id'].every(key => a[key] === b[key]);

export async function loadABVSnapshot(api: any, sessionId: string, systemId: number, preferredExecutionId?: string | null): Promise<ABVSnapshot> {
  const session = await api.getSession(sessionId);
  if (session?.id !== sessionId) throw new Error('Conversation changed while loading');
  const raw = session.context?.abv_proposal_reference;
  const reference: ABVReference | null = raw && typeof raw.execution_id === 'string' &&
    typeof raw.fingerprint === 'string' && Number.isInteger(raw.revision) && raw.target_system_id === systemId ? raw : null;
  const owner = reference ? await api.getExecution(reference.execution_id) : null;
  const planner = owner?.planner_metadata;
  const ownerReference = planner?.abv_proposal_reference ?? planner?.execution_plan?.builder?.proposal_reference;
  const ownerMatches = reference && owner?.id === reference.execution_id && owner?.session_id === sessionId && sameReference(reference, ownerReference);
  const pkg = ownerMatches ? (planner?.execution_plan?.builder?.design_package ?? planner?.design_package) : null;
  const bundle = pkg?._resolved_build;
  const nativeSystem = bundle?.candidate?.system;
  const config = nativeSystem?.config;
  const executions = [...(session.executions ?? [])].sort((a: any, b: any) =>
    String(a.started_at ?? '').localeCompare(String(b.started_at ?? '')));
  let activityId = preferredExecutionId ?? executions[executions.length - 1]?.id ?? reference?.build_execution_id ?? reference?.execution_id ?? null;
  if (!preferredExecutionId && reference?.build_execution_id && activityId === reference.execution_id) activityId = reference.build_execution_id;
  const activity = activityId === owner?.id ? owner : activityId ? await api.getExecutionSummary(activityId) : null;
  if (activity?.session_id && activity.session_id !== sessionId) throw new Error('Execution belongs to another conversation');
  const running = ['RUNNING', 'PENDING', 'QUEUED'].includes(activity?.status);
  const result = activity?.result ?? {};
  const outcome = result.builder_outcome ?? result.build_summary ?? result.architect_builder_validator_runtime ?? {};
  let status = result.workflow_status ?? result.architect_builder_validator_runtime?.status ?? null;
  if (running) status = reference?.build_execution_id === activityId || preferredExecutionId === activityId ? 'installing' : 'preparing';
  else if (['ERROR', 'FAILED'].includes(activity?.status)) status = 'execution_failed';
  else if (['CANCELLED', 'STOPPED'].includes(activity?.status)) status = 'execution_cancelled';
  if (status === 'architect_builder_validator_built' && (activity?.status !== 'COMPLETED' || outcome.validated !== true)) status = 'installation_unverified';
  if (status === 'awaiting_user_approval' && (!ownerMatches || !bundle || activityId !== reference?.execution_id || reference?.build_execution_id)) status = 'proposal_revision_changed';
  const bool = (value: any) => typeof value === 'boolean' ? value : null;
  const facts = config ? {
    fixedAllocation: config.fixed_risk_percent_per_trade != null ? `Fixed risk allocation per approved trade: ${config.fixed_risk_percent_per_trade}%` : null,
    maximum: config.max_risk_percent_per_trade != null ? `Maximum risk per trade: ${config.max_risk_percent_per_trade}%` : null,
    rewardRisk: config.min_reward_risk_ratio != null ? `Minimum reward/risk: ${config.min_reward_risk_ratio}:1` : null,
    orderType: config.order_type ? `Order type: ${config.order_type}` : null,
    inactiveOnInstall: typeof nativeSystem.is_active === 'boolean' ? !nativeSystem.is_active : bundle.construction === true ? true : null,
    approvalOn: bool(config.require_approval),
    schedulingDeferred: Array.isArray(bundle.candidate.schedule_intent) ? true : null,
    symbol: config.symbol ? `Symbol: ${config.symbol}` : null,
    accountId: config.account_id ? `Account: ${String(config.account_id).slice(0, 8)}…` : null,
  } : null;
  const methods = (pkg?.experts ?? []).map((expert: any) => ({ key: expert.key, name: expert.name, kind: expert.kind,
    inputs: [...(expert.evidence ?? []), ...(expert.receives ?? [])],
    prompt: pkg.prompt_blocks?.[expert.prompt_ref]?.text ?? null,
    refreshPolicy: expert.refresh_policy?.refresh_on === 'new_completed_bar'
      ? `On each new completed ${expert.refresh_policy.timeframe} bar (safety ceiling: never reused beyond ${expert.refresh_policy.max_age ?? 'three bars'})`
      : null }));
  const savedSystem = pkg?.system ?? null;
  const operatingSelection = savedSystem?.account_id && savedSystem?.symbol
    ? { accountId: String(savedSystem.account_id), symbol: String(savedSystem.symbol) }
    : null;
  return { sessionId, reference: ownerMatches ? reference : null, workflowStatus: status, policyFacts: facts, activityId, running, methods, operatingSelection };
}
